import { type ChildProcess, execFile, spawn } from "node:child_process";
import type { EventEmitter } from "node:events";
import fs from "node:fs/promises";
import path from "node:path";
import { createInterface, type Interface } from "node:readline";
import type { DriverTarget } from "@skaile/workspaces/plugin-registry";
import type { Logger } from "@skaile/workspaces/types";
import * as z from "zod";
import { classifyGenericErrorMessage } from "../error-classifier.js";
import { getBridgeLogger } from "../logger.js";
import type { ModelEntry } from "../models.js";
import { BUILTIN_DRIVER_CATALOG } from "../registry.js";
import { type AgentConfig, AgentDriver, type AgentError, type DriverInfo } from "../types.js";

/**
 * Process-level vars omp needs to run at all, independent of any provider.
 */
const OMP_BASE_ENV_KEYS = ["PATH", "HOME", "USER", "HOSTNAME", "SKAILE_SESSION_ID"] as const;

/**
 * Credential and routing env vars forwarded to the omp child process.
 *
 * omp is model-agnostic — it selects a provider from whichever of these it
 * finds. Forwarding only `ANTHROPIC_API_KEY`, as this allowlist used to,
 * silently reduced every non-Anthropic workspace to a fallback: the child had
 * no credential for the provider it was configured for and no way to say so.
 * It also omitted `ANTHROPIC_BASE_URL`, so an Anthropic-compatible gateway was
 * bypassed and traffic went to Anthropic's own API instead.
 *
 * The list mirrors the "Environment Variables" section of `omp --help`, which
 * is the authoritative statement of what the child actually reads. Widening it
 * is deliberate: these names *are* the provider credentials, so a host that
 * sets one in the runner's environment is asking for the agent to use it. Vars
 * outside this list — anything a platform provisions under its own naming —
 * still do not reach the child.
 *
 * @docLink packages/bridge/concepts#omp-env-whitelist
 */
export const OMP_PROVIDER_ENV_KEYS = [
  // ── Anthropic (direct, gateway, and Foundry/mTLS transports) ──────────────
  "ANTHROPIC_API_KEY",
  "ANTHROPIC_AUTH_TOKEN",
  "ANTHROPIC_OAUTH_TOKEN",
  "ANTHROPIC_BASE_URL",
  "ANTHROPIC_CUSTOM_HEADERS",
  "ANTHROPIC_FOUNDRY_API_KEY",
  "FOUNDRY_BASE_URL",
  "CLAUDE_CODE_USE_FOUNDRY",
  "CLAUDE_CODE_CLIENT_CERT",
  "CLAUDE_CODE_CLIENT_KEY",
  "NODE_EXTRA_CA_CERTS",

  // ── Other first-party model providers ─────────────────────────────────────
  "OPENAI_API_KEY",
  "OPENAI_BASE_URL",
  "GEMINI_API_KEY",
  "COPILOT_GITHUB_TOKEN",
  "AZURE_OPENAI_API_KEY",
  "GROQ_API_KEY",
  "CEREBRAS_API_KEY",
  "XAI_API_KEY",
  "MISTRAL_API_KEY",
  "ZAI_API_KEY",
  "MINIMAX_API_KEY",
  "DEEPSEEK_API_KEY",

  // ── Aggregators / gateways ────────────────────────────────────────────────
  "OPENROUTER_API_KEY",
  "KILO_API_KEY",
  "AI_GATEWAY_API_KEY",
  "OPENCODE_API_KEY",
  "CURSOR_ACCESS_TOKEN",
  "CLINE_API_KEY",
  "WAFER_SERVERLESS_API_KEY",
  "YOLO_AUTO_API_KEY",
  "ABLITERATION_API_KEY",
  "UMANS_AI_CODING_PLAN_API_KEY",
  "UMANS_WEBSEARCH_PROVIDER",

  // ── Cloud model planes ────────────────────────────────────────────────────
  "AWS_PROFILE",
  "AWS_ACCESS_KEY_ID",
  "AWS_SECRET_ACCESS_KEY",
  "AWS_SESSION_TOKEN",
  "AWS_REGION",
  "AWS_DEFAULT_REGION",
  "GOOGLE_CLOUD_PROJECT",
  "GOOGLE_CLOUD_LOCATION",
  "GOOGLE_APPLICATION_CREDENTIALS",

  // ── Search / tool backends ────────────────────────────────────────────────
  "EXA_API_KEY",
  "BRAVE_API_KEY",
  "PERPLEXITY_API_KEY",
  "PERPLEXITY_COOKIES",
  "TAVILY_API_KEY",
  "TINYFISH_API_KEY",
  "FIRECRAWL_API_KEY",
  "ANTHROPIC_SEARCH_API_KEY",
  "ANTHROPIC_SEARCH_BASE_URL",

  // ── omp's own configuration ───────────────────────────────────────────────
  "OMP_PROFILE",
] as const;

/**
 * Build the explicit env allowlist forwarded to the omp child process.
 *
 * The runner's full environment is never inherited — only
 * {@link OMP_BASE_ENV_KEYS} (process essentials),
 * {@link OMP_PROVIDER_ENV_KEYS} (provider credentials and routing), and the
 * `PI_*` prefix (omp's own app config the host operator may have set).
 *
 * Test seams (`OMP_BRIDGE_*`, `FAKE_OMP_*`) are passed through so the
 * subprocess harness in `tests/omp-driver.test.ts` can drive a fake-omp
 * script via env-driven mode flags.
 *
 * @docLink packages/bridge/concepts#omp-env-whitelist
 */
export function buildOmpChildEnv(parent: NodeJS.ProcessEnv): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of [...OMP_BASE_ENV_KEYS, ...OMP_PROVIDER_ENV_KEYS]) {
    const v = parent[key];
    if (typeof v === "string") out[key] = v;
  }
  for (const [key, value] of Object.entries(parent)) {
    if (typeof value !== "string") continue;
    if (key.startsWith("PI_") || key.startsWith("OMP_BRIDGE_") || key.startsWith("FAKE_OMP_")) {
      out[key] = value;
    }
  }
  return out;
}

/**
 * omp's built-in tools, as reported by `omp --help` ("Available Tools").
 *
 * `computer` is the one entry omp does not enable by default; everything else
 * is on unless `--tools` narrows the set. Used to turn a manifest *deny* list
 * into the *allow* list omp's CLI actually accepts.
 */
export const OMP_BUILTIN_TOOLS = [
  "read",
  "bash",
  "edit",
  "write",
  "grep",
  "glob",
  "lsp",
  "python",
  "notebook",
  "browser",
  "computer",
  "task",
  "todo",
  "web_search",
  "ask",
] as const;

/** omp's default-enabled toolset — every built-in except `computer`. */
export const OMP_DEFAULT_TOOLS: readonly string[] = OMP_BUILTIN_TOOLS.filter(
  (t) => t !== "computer",
);

/**
 * Canonical (Claude Code) tool name → omp built-in tool name.
 *
 * Agent manifests are authored once in the canonical vocabulary and run on
 * whichever backend the workspace is configured for, so the driver owns the
 * translation. Names already in omp's own spelling pass through unchanged.
 *
 * Deliberately partial: Claude tools with no omp counterpart (`WebFetch`,
 * `BashOutput`, `KillShell`, `mcp__*` server tools) are absent and get dropped
 * from the omp `--tools` value — omp's `--tools` governs built-ins only, so
 * naming a non-built-in there would be rejected, and silently keeping it would
 * not restrict anything.
 */
const CANONICAL_TO_OMP_TOOL: Record<string, string> = {
  read: "read",
  write: "write",
  edit: "edit",
  multiedit: "edit",
  notebookedit: "notebook",
  bash: "bash",
  grep: "grep",
  glob: "glob",
  ls: "glob",
  task: "task",
  agent: "task",
  todowrite: "todo",
  todoread: "todo",
  websearch: "web_search",
  web_search: "web_search",
  notebook: "notebook",
  lsp: "lsp",
  python: "python",
  browser: "browser",
  computer: "computer",
  todo: "todo",
  ask: "ask",
  askuserquestion: "ask",
};

/**
 * Translate a manifest tool list into omp built-in tool names.
 *
 * Unknown names are dropped rather than passed through: a manifest is expected
 * to run on more than one backend, so it legitimately names tools this one has
 * never heard of.
 */
function toOmpToolNames(names: readonly string[] | undefined): string[] {
  const out: string[] = [];
  for (const raw of names ?? []) {
    const mapped = CANONICAL_TO_OMP_TOOL[raw.trim().toLowerCase().replace(/[\s-]/g, "")];
    if (mapped && !out.includes(mapped)) out.push(mapped);
  }
  return out;
}

/**
 * Resolve `AgentConfig.tools` into the omp CLI flags that enforce it.
 *
 * omp expresses tool restriction as a positive `--tools a,b,c` list (or
 * `--no-tools`) — there is no deny flag — so a manifest deny list is applied by
 * subtracting from {@link OMP_DEFAULT_TOOLS}.
 *
 * Returns `[]` when the manifest declares no restriction, which leaves omp on
 * its full default toolset exactly as before. That default toolset is the bulk
 * of every request's fixed token prefix, so this is the lever an agent
 * definition needs in order to shrink it.
 *
 * Pure — exported for direct testing.
 *
 * @since 1.5.0
 */
export function buildOmpToolArgs(tools: AgentConfig["tools"]): string[] {
  const allowed = toOmpToolNames(tools?.allowed);
  const denied = new Set(toOmpToolNames(tools?.denied));

  const declaredAllow = (tools?.allowed?.length ?? 0) > 0;
  const declaredDeny = (tools?.denied?.length ?? 0) > 0;
  if (!declaredAllow && !declaredDeny) return [];

  const base = declaredAllow ? allowed : [...OMP_DEFAULT_TOOLS];
  const effective = base.filter((t) => !denied.has(t));

  // An allow list that named only tools omp does not have, or a deny list that
  // removed everything, both mean "no tools" — say so explicitly rather than
  // emitting `--tools ` and letting omp fall back to the full default set.
  if (effective.length === 0) return ["--no-tools"];
  return ["--tools", effective.join(",")];
}

interface OmpEvent {
  type: string;
  data?: unknown;
  id?: string;
  success?: boolean;
  error?: string;
  message?: OmpMessage;
  [key: string]: unknown;
}

interface OmpMessage {
  role?: string;
  content?: string | OmpContentBlock[];
  /**
   * Why the turn stopped. omp reports a failed request as a normal
   * `message_end` carrying `stopReason: "error"` rather than as an `error`
   * event, so this is the only signal that the turn failed.
   */
  stopReason?: string;
  /** Provider/transport error text accompanying `stopReason: "error"`. */
  errorMessage?: string;
}

interface OmpContentBlock {
  type: string;
  text?: string;
  id?: string;
  name?: string;
  input?: unknown;
  thinking?: string;
}

/**
 * Agent driver for the oh-my-pi (omp) coding agent.
 *
 * Spawns `omp --mode rpc` as a child process and communicates via newline-delimited
 * JSON-RPC over stdin/stdout. Model-agnostic: supports any LLM provider.
 *
 * @see https://github.com/can1357/oh-my-pi
 *
 * @remarks
 * **Session persistence** — omp writes JSONL session files to `<cwd>/.omp/sessions/`
 * (via `--session-dir`). After each turn (`agent_end`), sends a `get_state` RPC to
 * capture the native session ID and emits a `session_info` event so the forge layer
 * can persist it. On the next spawn, callers pass `resumeSessionId` to restore omp's
 * native context.
 *
 * **stderrBuffer** — collects the last 20 lines of stderr so they can be appended to
 * crash error messages for diagnostics without blocking the stdout readline loop.
 *
 * **systemPrompt injection** — omp does not accept a system prompt string directly.
 * The driver writes `AgentConfig.systemPrompt` to `<cwd>/.omp/system.md` and passes
 * the path via `--append-system-prompt` before spawning.
 *
 * **agentDir** — maps to the `PI_CODING_AGENT_DIR` environment variable. omp reads the
 * GitAgent directory at this path natively (agent.yaml, SOUL.md, RULES.md, knowledge/).
 *
 * @docLink packages/bridge/drivers#omp-driver
 */
export class OmpDriver extends AgentDriver {
  readonly driverInfo: DriverInfo = {
    id: "omp",
    name: "omp (oh-my-pi)",
    modelAgnostic: true,
    supportsInBandAbort: true,
  };

  private readonly log: Logger = getBridgeLogger("omp");
  private proc: ChildProcess | null = null;
  private rl: Interface | null = null;
  private reqId = 0;
  private config: AgentConfig;
  private ready = false;
  private startPromise: Promise<void> | null = null;
  private stderrBuffer: string[] = [];
  private prevText = "";
  /** OMP session ID captured via `get_state` after each turn. */
  private _ompSessionId?: string;
  /** Per-turn settlement handlers; non-null only while a prompt is in flight. */
  private turnResolve: (() => void) | null = null;
  private turnReject: ((err: Error) => void) | null = null;

  override get runtimeSessionId(): string | undefined {
    return this._ompSessionId;
  }

  override getModel(): string | undefined {
    return this.config.model;
  }

  /**
   * @param config - Driver configuration. omp-relevant fields:
   *   - `cwd` — working directory; omp session files are stored under `<cwd>/.omp/sessions/`.
   *   - `model` / `provider` — forwarded as `--model [provider/]model`.
   *   - `systemPrompt` — written to `<cwd>/.omp/system.md` and appended via `--append-system-prompt`.
   *   - `agentDir` — sets `PI_CODING_AGENT_DIR`; omp reads agent identity from this directory.
   *   - `sshKeyPath` — injected as `GIT_SSH_COMMAND` for authenticated git operations.
   *   - `resumeSessionId` — omp session UUID to restore via `--session <id>`.
   */
  constructor(config: AgentConfig) {
    super();
    this.config = config;
  }

  public async start(): Promise<void> {
    if (this.proc) return;
    if (this.startPromise) return this.startPromise;
    this.startPromise = this._start();
    return this.startPromise;
  }

  private async _start(): Promise<void> {
    const args = ["--mode", "rpc"];

    // ── Session persistence ───────────────────────────────────────────────────
    // Store OMP session files in <cwd>/.omp/sessions/ so they survive process
    // restarts. The caller passes resumeSessionId to restore a prior session.
    const sessionDir = path.join(this.config.cwd, ".omp", "sessions");
    await fs.mkdir(sessionDir, { recursive: true });
    args.push("--session-dir", sessionDir);

    if (this.config.resumeSessionId) {
      this.log.info("resuming OMP session", { sessionId: this.config.resumeSessionId });
      args.push("--session", this.config.resumeSessionId);
    }

    if (this.config.model) {
      const modelArg = this.config.provider
        ? `${this.config.provider}/${this.config.model}`
        : this.config.model;
      args.push("--model", modelArg);
    }

    // ── Tool restrictions ─────────────────────────────────────────────────────
    // agent.yaml `tools:` reaches the driver as AgentConfig.tools. The
    // claude-sdk driver has always honoured it; omp did not, so a restricted
    // agent definition silently ran with omp's full default toolset — which
    // dominates the fixed token prefix of every request.
    const toolArgs = buildOmpToolArgs(this.config.tools);
    if (toolArgs.length > 0) {
      this.log.info("applying tool restrictions", { args: toolArgs });
      args.push(...toolArgs);
    }

    // ── System prompt injection ───────────────────────────────────────────────
    // omp does not accept a system prompt string directly; it reads from a file.
    // We write it to .omp/system.md (inside the project cwd) and pass the path
    // via --append-system-prompt.  This file is overwritten on each driver start.
    //
    // In flow mode (factory-assets/connectors/flow/run-flow.ts conductFlow), systemPrompt carries the full agent
    // identity (SOUL + RULES + knowledge + connector docs) because no agentDir is set.
    // In REPL mode (agent.ts buildDriver), systemPrompt carries only connector docs
    // because PI_CODING_AGENT_DIR already gives omp the full agent identity.
    if (this.config.systemPrompt) {
      const ompDir = path.join(this.config.cwd, ".omp");
      await fs.mkdir(ompDir, { recursive: true });
      const systemFile = path.join(ompDir, "system.md");
      await fs.writeFile(systemFile, this.config.systemPrompt);
      args.push("--append-system-prompt", systemFile);
    }

    // Explicit env whitelist for the omp child process. Avoids leaking the
    // runner's full environment (which may carry user secrets provisioned by
    // the platform) into the spawned subprocess. `this.config.env`, when
    // supplied, fully overrides the whitelist (used by tests + advanced
    // callers).
    const env: Record<string, string> = this.config.env
      ? { ...(this.config.env as Record<string, string>) }
      : buildOmpChildEnv(process.env);
    // ── Main-agent handoff ────────────────────────────────────────────────────
    // PI_CODING_AGENT_DIR is the omp equivalent of `claude --agent <name>`.
    // omp reads the GitAgent directory at this path natively: agent.yaml for the
    // manifest, SOUL.md for identity, RULES.md for constraints, knowledge/ for domain
    // context.  The path may be the source directory (agentDir in REPL mode) or a
    // rendered copy installed by ompRenderer under .omp/agents/<name>/.
    // Unlike the claude-sdk path, no prior `skaile install` is required for REPL mode
    // because omp reads the source directory directly via this env variable.
    if (this.config.agentDir) {
      env.PI_CODING_AGENT_DIR = this.config.agentDir;
    }
    if (this.config.sshKeyPath) {
      env.GIT_SSH_COMMAND = `ssh -i "${this.config.sshKeyPath}" -o StrictHostKeyChecking=accept-new`;
    }

    // test-only: `OMP_BRIDGE_BIN` overrides the binary name and `OMP_BRIDGE_PREARGS`
    // (space-delimited) supplies any leading args (e.g. a JS script path when the
    // override binary is `node` or `bun`). Used by the subprocess harness tests in
    // `tests/omp-driver.test.ts` to point spawn at a fake-omp script without
    // monkey-patching `node:child_process`. Both are ignored in production.
    const overrideBin = process.env.OMP_BRIDGE_BIN;
    const overridePreArgs = process.env.OMP_BRIDGE_PREARGS
      ? process.env.OMP_BRIDGE_PREARGS.split(/\s+/).filter(Boolean)
      : [];
    const spawnBin = overrideBin || "omp";
    const spawnArgs = overrideBin ? [...overridePreArgs, ...args] : args;

    return new Promise<void>((resolve, reject) => {
      this.log.info("spawning omp", {
        bin: spawnBin,
        args: spawnArgs,
        cwd: this.config.cwd,
      });
      if (env.PI_CODING_AGENT_DIR)
        this.log.debug("env: PI_CODING_AGENT_DIR set", { value: env.PI_CODING_AGENT_DIR });
      if (env.GIT_SSH_COMMAND) this.log.debug("env: GIT_SSH_COMMAND set");

      // Cast to ChildProcess: spawn infers ChildProcessWithoutNullStreams for
      // pipe stdio, but bun-types doesn't expose EventEmitter.on on that subtype.
      const proc = spawn(spawnBin, spawnArgs, {
        cwd: this.config.cwd,
        env,
        stdio: ["pipe", "pipe", "pipe"],
      }) as ChildProcess;
      this.proc = proc;
      // bun-types 1.3.11 omits EventEmitter.on from ChildProcess; cast to subscribe.
      const procEmitter = proc as unknown as EventEmitter;

      procEmitter.on("error", (err: Error) => {
        const detail = this.classifyProcessError(err as NodeJS.ErrnoException);
        this.log.error("process error", err, { detail });
        this.emit("agent-event", { type: "error", error: detail.message, detail });
        if (!this.ready) reject(err);
        // Settle any in-flight turn so a process-level error cannot hang prompt().
        this.rejectTurn(Object.assign(new Error(detail.message), { detail }));
      });

      procEmitter.on("exit", (code: number | null, signal: NodeJS.Signals | null) => {
        this.log.info("process exited", { code, signal });
        const lastStderr = this.stderrBuffer.slice(-10).join("\n");
        const crashed = code !== null && code !== 0;
        if (crashed) {
          const detail: AgentError = {
            message: `omp process crashed (exit code ${code})${lastStderr ? `: ${lastStderr}` : ""}`,
            category: "process",
            retryable: true,
            hint:
              lastStderr || "The agent process exited unexpectedly. Try sending your prompt again.",
          };
          this.emit("agent-event", { type: "error", error: detail.message, detail });
        }
        this.cleanup();
        // Settle any in-flight turn: an exit before `agent_end` means the turn
        // can never complete normally, so reject rather than leak a pending promise.
        this.rejectTurn(
          new Error(
            crashed
              ? `omp process crashed (exit code ${code})`
              : `omp process exited before the turn completed${signal ? ` (signal ${signal})` : ""}`,
          ),
        );
        this.emit("exit", { code, signal });
      });

      if (proc.stdout) {
        const rl = createInterface({ input: proc.stdout });
        this.rl = rl;
        // bun-types 1.3.11 omits EventEmitter.on from readline.Interface; cast to subscribe.
        (rl as unknown as EventEmitter).on("line", (line: string) => {
          if (!line.trim()) return;
          try {
            const event: OmpEvent = JSON.parse(line);
            this.handleEvent(event);
          } catch {
            this.log.debug("stdout", { line });
          }
        });
      }

      if (proc.stderr) {
        const stderrRl = createInterface({ input: proc.stderr });
        (stderrRl as unknown as EventEmitter).on("line", (line: string) => {
          this.log.warn("stderr", { line });
          this.stderrBuffer.push(line);
          if (this.stderrBuffer.length > 20) this.stderrBuffer.shift();
        });
      }

      const startTime = Date.now();
      const readyTimeout = setTimeout(() => {
        if (!this.ready) {
          this.log.warn("ready timeout, proceeding", { timeoutMs: 5000 });
          this.ready = true;
          resolve();
        }
      }, 5000);

      this.once("ready", () => {
        clearTimeout(readyTimeout);
        if (!this.ready) {
          this.log.info("process ready", { elapsedMs: Date.now() - startTime });
          this.ready = true;
          resolve();
        }
      });
    });
  }

  private extractMessageText(msg: OmpMessage): string {
    if (!msg) return "";
    const c = msg.content;
    if (typeof c === "string") return c;
    if (Array.isArray(c))
      return c
        .filter((b) => b.type === "text" && b.text)
        .map((b) => b.text)
        .join("");
    return "";
  }

  private handleEvent(event: OmpEvent) {
    if (event.type === "ready") {
      this.emit("ready");
      return;
    }
    if (event.type === "response") {
      if (!event.success && event.error) {
        const detail = this.classifyErrorMessage(event.error);
        this.emit("agent-event", { type: "error", error: event.error, detail });
      }
      this.emit(`response:${event.id}`, event);
      return;
    }
    if (event.type === "error" && event.error) {
      const detail = this.classifyErrorMessage(event.error);
      this.emit("agent-event", { ...event, detail });
      return;
    }
    // Compute text delta for message_update events
    if (event.type === "message_update" && event.message) {
      const text = this.extractMessageText(event.message);
      let delta = "";
      if (text.startsWith(this.prevText)) {
        delta = text.slice(this.prevText.length);
      } else {
        // Content restructured (e.g. thinking blocks changed) — emit full text as delta
        delta = text;
      }
      this.prevText = text;
      this.emit("agent-event", { ...event, _textDelta: delta });
      return;
    }
    // Reset delta tracking on message boundaries. `message_start` may already
    // carry content (omp emits a partially-populated message when resuming or
    // when the first chunk lands with the start frame); seeding prevText from
    // it rather than clearing keeps the next message_update from re-emitting
    // that text as a delta.
    if (event.type === "message_start") {
      this.prevText = event.message ? this.extractMessageText(event.message) : "";
    }
    if (event.type === "message_end" || event.type === "agent_end") {
      this.prevText = "";
    }

    // A failed omp request arrives as `message_end` with `stopReason: "error"`,
    // not as an `error` event. Without this branch the turn ended silently: the
    // caller saw a normal completion with no assistant text and no failure,
    // which reads as a hung or empty agent. Surface it on the same channel as
    // every other driver error so callers can classify and retry.
    if (
      event.type === "message_end" &&
      event.message?.role === "assistant" &&
      event.message.stopReason === "error"
    ) {
      const error = event.message.errorMessage || "omp request failed";
      const detail = this.classifyErrorMessage(error);
      this.log.error("omp request failed", new Error(error), { detail });
      this.emit("agent-event", { type: "error", error, detail });
    }

    // After each turn ends, capture the OMP session ID so callers can persist it
    // and pass it as resumeSessionId on the next spawn.
    if (event.type === "agent_end") {
      void this.captureSessionId();
    }
    this.emit("agent-event", event);
    // Resolve AFTER emitting so listeners observing the turn see `agent_end`
    // before the caller's `prompt()` await unblocks.
    if (event.type === "agent_end") {
      this.resolveTurn();
    }
  }

  /**
   * Resolve the in-flight prompt turn (on `agent_end`). Idempotent — a turn
   * settles at most once, so a stray second `agent_end` is harmless.
   */
  private resolveTurn(): void {
    const resolve = this.turnResolve;
    this.turnResolve = null;
    this.turnReject = null;
    resolve?.();
  }

  /**
   * Reject the in-flight prompt turn (process crash/error). Settling here keeps
   * `prompt()` from hanging forever when the child dies before `agent_end`.
   */
  private rejectTurn(err: Error): void {
    const reject = this.turnReject;
    this.turnResolve = null;
    this.turnReject = null;
    reject?.(err);
  }

  /**
   * Query OMP for the current session ID via `get_state` RPC.
   * Emits a `session_info` event on success so the forge layer can persist the ID.
   * Failures are silently swallowed — session tracking is best-effort.
   */
  private async captureSessionId(): Promise<void> {
    try {
      const id = this.nextId();
      const response = await new Promise<OmpEvent>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("get_state timeout")), 5000);
        this.once(`response:${id}`, (resp: OmpEvent) => {
          clearTimeout(timer);
          resolve(resp);
        });
        try {
          this.send({ id, type: "get_state" });
        } catch (err) {
          clearTimeout(timer);
          reject(err);
        }
      });

      if (response.success && response.data) {
        const data = response.data as Record<string, unknown>;
        const sessionId = data.sessionId as string | undefined;
        const sessionFile = data.sessionFile as string | undefined;
        if (sessionId) {
          this._ompSessionId = sessionId;
          this.log.debug("omp session id assigned", { sessionId });
          this.emit("agent-event", {
            type: "session_info",
            driverSessionId: sessionId,
            ...(sessionFile ? { sessionFile } : {}),
          });
        }
      }
    } catch {
      // get_state is best-effort — OMP may not support it yet or the process
      // may have exited. The short-term history injection fallback still works.
    }
  }

  private nextId(): string {
    return `req-${++this.reqId}`;
  }

  private send(msg: object): void {
    if (!this.proc?.stdin?.writable) {
      const detail: AgentError = {
        message: "omp process not running",
        category: "process",
        retryable: true,
        hint: "The agent process is not available. It will restart automatically on the next prompt.",
      };
      const err = Object.assign(new Error(detail.message), { detail });
      throw err;
    }
    this.proc.stdin.write(`${JSON.stringify(msg)}\n`);
  }

  /**
   * Sends a prompt to the omp process via JSON-RPC and resolves when the turn
   * completes (after the `agent_end` event), honoring the `AgentDriver.prompt`
   * contract so driver-agnostic callers can await turn completion uniformly.
   *
   * The driver emits `agent-event` payloads as omp streams responses on stdout.
   * If the child process errors or exits before `agent_end`, the returned promise
   * rejects rather than hanging.
   *
   * @param message - Plain-text user prompt.
   * @throws {Error} When the omp process is not running, its stdin is closed, or
   *   the child crashes/exits before the turn completes.
   */
  public async prompt(message: string): Promise<void> {
    await this.start();
    const id = this.nextId();

    // Install the per-turn resolver BEFORE sending: omp can stream a fast
    // `agent_end` on the next readline tick, and if the resolver isn't wired
    // yet that event would be missed and prompt() would hang forever.
    const turnPromise = new Promise<void>((resolve, reject) => {
      this.turnResolve = resolve;
      this.turnReject = reject;
    });

    try {
      this.send({ id, type: "prompt", message });
    } catch (err) {
      this.turnResolve = null;
      this.turnReject = null;
      throw err;
    }

    await turnPromise;
  }

  /**
   * Sends an in-band abort RPC to the omp process, requesting it to stop the
   * current turn without terminating the child process.
   *
   * @remarks This is a best-effort signal — omp may not honour the abort
   *   immediately. The process remains alive after `abort()`.
   */
  public async abort(): Promise<void> {
    if (!this.proc) return;
    const id = this.nextId();
    this.send({ id, type: "abort" });
  }

  /** `true` when the omp child process is alive and has not been killed. */
  public get isRunning(): boolean {
    return this.proc !== null && !this.proc.killed;
  }

  /**
   * Sends SIGTERM to the omp child process and performs cleanup.
   *
   * The driver instance must not be reused after `kill()`. Create a new
   * `OmpDriver` to start a fresh session.
   */
  kill(): void {
    if (this.proc) {
      this.proc.kill("SIGTERM");
      this.cleanup();
    }
  }

  async resetSession(): Promise<void> {
    this.kill();
    await this.start();
  }

  private classifyProcessError(err: NodeJS.ErrnoException): AgentError {
    if (err.code === "ENOENT") {
      return {
        message: "omp command not found",
        category: "config",
        retryable: false,
        hint: "The omp agent is not installed or not on your PATH. Install it and try again.",
      };
    }
    if (err.code === "EACCES") {
      return {
        message: "Permission denied running omp",
        category: "config",
        retryable: false,
        hint: "Check file permissions for the omp binary.",
      };
    }
    return {
      message: err.message,
      category: "process",
      retryable: true,
      hint: "An unexpected process error occurred. Try sending your prompt again.",
    };
  }

  private classifyErrorMessage(msg: string): AgentError {
    // Shared keyword buckets live in error-classifier.ts; only the hint copy is omp-specific.
    const generic = classifyGenericErrorMessage(msg);
    if (!generic) return { message: msg, category: "unknown", retryable: true };

    const lower = msg.toLowerCase();
    const hint =
      generic.category === "auth"
        ? "Check your API key configuration in Settings."
        : generic.category === "rate_limit"
          ? "You have hit the provider rate limit. Wait a moment and try again."
          : generic.category === "network"
            ? "A network error occurred. Check your connection and try again."
            : // model: distinguish unavailable-model from over-long-context.
              lower.includes("model")
              ? "The selected model is not available. Change the model in settings."
              : "The conversation is too long. Start a new session or choose a model with a larger context window.";
    return { message: msg, category: generic.category, retryable: generic.retryable, hint };
  }

  override async listModels(): Promise<ModelEntry[]> {
    return new Promise((resolve, reject) => {
      execFile("omp", ["--list-models"], { timeout: 10_000 }, (err, stdout, stderr) => {
        if (err) {
          reject(new Error(`omp --list-models failed: ${stderr || err.message}`));
          return;
        }
        const models: ModelEntry[] = [];
        let inCanonical = false;
        let headerSkipped = false;
        for (const raw of stdout.split("\n")) {
          const line = raw.trimEnd();
          if (!inCanonical) {
            if (line.startsWith("Canonical models")) inCanonical = true;
            continue;
          }
          if (!headerSkipped) {
            headerSkipped = true;
            continue;
          }
          // blank line or a new section header ends the canonical block
          if (line === "" || (!line.startsWith(" ") && line.endsWith("models"))) break;
          const canonical = line.split(/\s{2,}/)[0]?.trim();
          if (canonical) models.push({ id: canonical, name: canonical });
        }
        resolve(models);
      });
    });
  }

  private cleanup(): void {
    this.rl?.close();
    this.rl = null;
    this.proc = null;
    this.ready = false;
    this.startPromise = null;
    this.stderrBuffer = [];
  }
}

/**
 * `DriverTarget` wrapper for the omp driver. Registered into the plugin
 * registry by `registerBuiltinDrivers()`.
 */
export const ompDriverTarget: DriverTarget<AgentConfig> = {
  id: "omp",
  displayName: BUILTIN_DRIVER_CATALOG.omp?.name ?? "omp (oh-my-pi)",
  apiVersion: 1,
  // Drivers take AgentConfig; passthrough — full schema validation is out of scope.
  configSchema: z.custom<AgentConfig>(() => true),
  create: async (config, _ctx) => new OmpDriver(config),
  listModels: async (apiKeys) =>
    new OmpDriver({ cwd: process.cwd(), sessionId: "model-list", apiKeys }).listModels(),
};
